import clsx from "clsx";
import {
  FC,
  KeyboardEvent,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { useEventListener } from "@tsmono/react/hooks";
import { Icon } from "@tsmono/react/icons";

import styles from "./EventNavsPicker.module.css";

interface EventNavsPickerProps {
  navs: Array<{ id: string; title: string; target: string }>;
  selectedNav: string;
  setSelectedNav: (target: string) => void;
  /** Natural width (px) of the longest option's text — sets the label's
   *  preferred width so the button is wide enough to fit any selection
   *  without truncation when there's room. */
  labelWidth?: number;
}

/**
 * Compact replacement for {@link EventNavs} when the tab row is too narrow
 * to display all pills inline. Renders the active tab title as a single
 * button that opens a portal-anchored menu of all navs.
 */
export const EventNavsPicker: FC<EventNavsPickerProps> = ({
  navs,
  selectedNav,
  setSelectedNav,
  labelWidth,
}) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState<{
    top: number;
    right: number;
    minWidth: number;
  } | null>(null);

  const computePosition = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPosition({
      top: rect.bottom,
      right: window.innerWidth - rect.right,
      minWidth: rect.width,
    });
  }, []);

  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useLayoutEffect(() => {
    if (open) computePosition();
  }, [open, computePosition]);

  const windowTarget = open && typeof window !== "undefined" ? window : null;
  useEventListener(windowTarget, "resize", computePosition);
  useEventListener(windowTarget, "scroll", computePosition, { capture: true });

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      buttonRef.current?.focus();
    }
  }, []);

  const selected = navs.find((nav) => nav.target === selectedNav) ?? navs[0];

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        className={clsx(
          "nav-link",
          "active",
          "text-style-label",
          "text-size-small",
          styles.trigger
        )}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={handleKeyDown}
      >
        <span
          className={styles.label}
          style={labelWidth ? { width: labelWidth } : undefined}
        >
          {selected?.title ?? ""}
        </span>
        <Icon name="chevronDown" className={styles.chevron} />
      </button>
      {open &&
        position &&
        createPortal(
          <>
            {/* Mouse-only dismissal; Escape closes the picker. */}
            <div
              className={styles.backdrop}
              role="presentation"
              onClick={() => setOpen(false)}
            />
            <div
              role="listbox"
              tabIndex={-1}
              className={styles.menu}
              style={{
                top: position.top,
                right: position.right,
                minWidth: position.minWidth,
              }}
              onKeyDown={handleKeyDown}
            >
              {navs.map((nav) => {
                const isSelected = nav.target === selectedNav;
                return (
                  <button
                    key={nav.id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={clsx(
                      styles.item,
                      isSelected ? styles.selected : undefined,
                      "text-size-small"
                    )}
                    onClick={() => {
                      setSelectedNav(nav.target);
                      setOpen(false);
                    }}
                  >
                    {nav.title}
                  </button>
                );
              })}
            </div>
          </>,
          document.body
        )}
    </>
  );
};
